import React from 'react';

interface DropdownItemProps {
  /** Icon element displayed before the label */
  icon?: React.ReactNode;
  /** Primary label text */
  label: string;
  /** Secondary label (displayed below or after primary) */
  sublabel?: string;
  /** Right-side content (e.g. spinner, badge, status indicator) */
  trailing?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Disabled state */
  disabled?: boolean;
  /** Additional className */
  className?: string;
}

export function DropdownItem({
  icon,
  label,
  sublabel,
  trailing,
  onClick,
  disabled = false,
  className = '',
}: DropdownItemProps) {
  // A disabled item dims its icon, label and trailing content, but not the
  // sublabel: that is often the reason it is disabled, and must stay readable
  const dimmed = disabled ? 'opacity-50' : '';

  return (
    <button
      type="button"
      className={`w-full flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-game-medium/60 transition-all duration-150 rounded-md group text-left
        ${disabled ? 'cursor-not-allowed' : ''}
        ${className}`}
      onClick={() => !disabled && onClick?.()}
      disabled={disabled}
    >
      {icon && (
        <span className={`flex-shrink-0 text-sm ${dimmed}`}>{icon}</span>
      )}
      <span className="flex-1 min-w-0">
        <span className={`text-gray-200 font-medium text-sm group-hover:text-white block truncate ${dimmed}`}>
          {label}
        </span>
        {sublabel && (
          // Wraps rather than truncating, for the same reason
          <span className="text-gray-200 text-xs block">{sublabel}</span>
        )}
      </span>
      {trailing && (
        <span className={`flex-shrink-0 ml-auto ${dimmed}`}>{trailing}</span>
      )}
    </button>
  );
}
